import React, { Fragment, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useUser } from "../context/UserContext";
import {
  signupUpdateAccountTypeWithData,
  updateUserAccountTypeWithData,
} from "../utils/utils";
import { getFriendlyApiError } from "../utils/auth";

const ACCOUNT_TYPES = [
  {
    name: "citizen",
    title: "Citizen",
    subtitle:
      "I want to follow policy, attend events, and engage with my community",
    recommended: true,
  },
  {
    name: "public_official_candidate",
    title: "Public Official / Candidate",
    subtitle:
      "I hold office, previously held office, or am running for office",
    requiresFollowUp: true,
  },
  {
    name: "organization",
    title: "Organization / Nonprofit",
    subtitle:
      "We speak for a team, cause, or membership, not as individuals",
  },
  {
    name: "municipality",
    title: "Municipality",
    subtitle: "We are a city, town, county, or government department",
    helperText: "Not for individual office-holders",
  },
];

const STATUS_OPTIONS = [
  { value: "incumbent", label: "Currently in Office" },
  { value: "former", label: "Former Office Holder" },
  { value: "first_time", label: "First-Time Candidate" },
];

const LEVEL_OPTIONS = [
  { value: "local", label: "Local Government" },
  { value: "state", label: "State Government" },
  { value: "federal", label: "Federal Government" },
];

const POSITION_OPTIONS = {
  federal: [
    { value: "us_senator", label: "U.S. Senator" },
    { value: "us_representative", label: "U.S. Representative" },
    { value: "other", label: "Other Federal Position" },
  ],
  state: [
    { value: "state_senator", label: "State Senator" },
    { value: "state_representative", label: "State Representative" },
    { value: "governor", label: "Governor" },
    { value: "lt_governor", label: "Lieutenant Governor" },
    { value: "attorney_general", label: "Attorney General" },
    { value: "secretary_of_state", label: "Secretary of State" },
    { value: "state_treasurer", label: "State Treasurer / Comptroller" },
    { value: "other", label: "Other Statewide Office" },
  ],
  local: [
    { value: "mayor", label: "Mayor" },
    { value: "city_council", label: "City Council Member" },
    { value: "county_commissioner", label: "County Commissioner" },
    { value: "school_board", label: "School Board Member" },
    { value: "town_supervisor", label: "Town Supervisor" },
    { value: "other", label: "Other Position" },
  ],
};

export default function AccountTypeSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    hasSelectedAccountType,
    loadUserContext,
    accountTypeName: contextAccountTypeName,
    publicOfficeStatus: contextOfficeStatus,
    publicOfficeLevel: contextOfficeLevel,
    publicOfficePosition: contextOfficePosition,
  } = useUser();

  const changeMode = location.state?.changeMode || false;
  const currentAccountTypeName = location.state?.currentAccountType;
  const cancelDestination = location.state?.cancelTo || "/account-details";
  const resumeStepFromState = Number(location.state?.resumeStep);
  const stateOfficeStatus = location.state?.public_office_status;
  const stateOfficeLevel = location.state?.public_office_level;
  const stateOfficePosition = location.state?.public_office_position;
  const initialOfficeStatusValue = stateOfficeStatus || contextOfficeStatus || null;
  const initialOfficeLevelValue = stateOfficeLevel || contextOfficeLevel || null;
  const initialOfficePositionValue = stateOfficePosition || contextOfficePosition || null;

  const getInitialSelectedType = () => {
    const typeName =
      currentAccountTypeName ||
      (changeMode ? contextAccountTypeName : null);
    if (!typeName) return null;
    return ACCOUNT_TYPES.find((t) => t.name === typeName) || null;
  };

  const getInitialStep = () => {
    if (Number.isInteger(resumeStepFromState) && resumeStepFromState >= 1 && resumeStepFromState <= 4) {
      return resumeStepFromState;
    }
    const typeName =
      currentAccountTypeName ||
      (changeMode ? contextAccountTypeName : null);
    if (typeName !== "public_official_candidate") return 1;
    if (initialOfficeStatusValue && initialOfficeLevelValue && initialOfficePositionValue) return 4;
    if (initialOfficeStatusValue && initialOfficeLevelValue) return 3;
    if (initialOfficeStatusValue) return 2;
    return 1;
  };

  const getInitialPositionState = () => {
    if (!initialOfficePositionValue) {
      return { officePosition: null, customPosition: "" };
    }
    const levelOptions = POSITION_OPTIONS[initialOfficeLevelValue] || [];
    const known = levelOptions.some((o) => o.value === initialOfficePositionValue);
    if (known) {
      return { officePosition: initialOfficePositionValue, customPosition: "" };
    }
    return { officePosition: "other", customPosition: initialOfficePositionValue };
  };

  const initialPositionState = getInitialPositionState();
  const [step, setStep] = useState(getInitialStep);
  const [selectedType, setSelectedType] = useState(getInitialSelectedType);
  const [officeStatus, setOfficeStatus] = useState(initialOfficeStatusValue);
  const [officeLevel, setOfficeLevel] = useState(initialOfficeLevelValue);
  const [officePosition, setOfficePosition] = useState(initialPositionState.officePosition);
  const [customPosition, setCustomPosition] = useState(initialPositionState.customPosition);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const nextButtonRef = useRef(null);

  useEffect(() => {
    if (hasSelectedAccountType && !changeMode) {
      navigate("/setup-representative", {
        replace: true,
        state: {
          accountTypeName: contextAccountTypeName,
          ...(contextOfficeStatus != null && {
            public_office_status: contextOfficeStatus,
          }),
          ...(contextOfficeLevel != null && {
            public_office_level: contextOfficeLevel,
          }),
          ...(contextOfficePosition != null && {
            public_office_position: contextOfficePosition,
          }),
        },
      });
    }
  }, [
    hasSelectedAccountType,
    changeMode,
    navigate,
    contextAccountTypeName,
    contextOfficeStatus,
    contextOfficeLevel,
    contextOfficePosition,
  ]);

  useEffect(() => {
    if (selectedType) return;
    if (!changeMode && !hasSelectedAccountType) return;

    const typeName =
      currentAccountTypeName || contextAccountTypeName || null;
    if (!typeName) return;

    const match = ACCOUNT_TYPES.find((t) => t.name === typeName);
    if (!match) return;

    setSelectedType(match);

  }, [
    selectedType,
    changeMode,
    hasSelectedAccountType,
    currentAccountTypeName,
    contextAccountTypeName,
    stateOfficeStatus,
    stateOfficeLevel,
    stateOfficePosition,
    contextOfficeStatus,
    contextOfficeLevel,
    contextOfficePosition,
  ]);

  useEffect(() => {
    if (selectedType?.name !== "public_official_candidate") return;

    const status = stateOfficeStatus || contextOfficeStatus || null;
    const level = stateOfficeLevel || contextOfficeLevel || null;
    const position = stateOfficePosition || contextOfficePosition || null;

    if (!officeStatus && status) setOfficeStatus(status);
    if (!officeLevel && level) setOfficeLevel(level);
    if ((!officePosition && !customPosition) && position) {
      const levelOptions = POSITION_OPTIONS[level] || [];
      const known = levelOptions.some((o) => o.value === position);
      if (known) {
        setOfficePosition(position);
        setCustomPosition("");
      } else {
        setOfficePosition("other");
        setCustomPosition(position);
      }
    }
  }, [
    selectedType,
    officeStatus,
    officeLevel,
    officePosition,
    customPosition,
    stateOfficeStatus,
    stateOfficeLevel,
    stateOfficePosition,
    contextOfficeStatus,
    contextOfficeLevel,
    contextOfficePosition,
  ]);

  useEffect(() => {
    if (step === 1 && selectedType && nextButtonRef.current) {
      nextButtonRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [step, selectedType]);

  const handleSelectType = (type) => {
    setSelectedType(type);
    setOfficeStatus(null);
    setOfficeLevel(null);
    setOfficePosition(null);
    setCustomPosition("");
    if (!type.requiresFollowUp && step > 1) {
      setStep(1);
    }
  };

  const getPositionValue = () =>
    officePosition === "other" ? customPosition.trim() : officePosition;

  const handleContinue = async () => {
    if (!selectedType) return;

    if (selectedType.requiresFollowUp) {
      if (step < 4) {
        if (step === 2 && !officeStatus) return;
        if (step === 3 && !officeLevel) return;
        setStep(step + 1);
        return;
      }
      if (!getPositionValue()) return;
    } else if (step !== 1) {
      setStep(1);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const additionalData = {};
      if (selectedType.name === "public_official_candidate") {
        additionalData.public_office_status = officeStatus;
        additionalData.public_office_level = officeLevel;
        additionalData.public_office_position = getPositionValue();
      }

      const payload = {
        account_type_name: selectedType.name,
        ...additionalData,
      };

      // Persist immediately — including in change mode — so account-type-gated
      // features (Campaigns, events) unlock even if the user leaves the flow
      // before finishing rep setup / username confirmation.
      const auth = getAuth();
      if (!auth.currentUser?.emailVerified) {
        await signupUpdateAccountTypeWithData(payload);
      } else {
        await updateUserAccountTypeWithData(payload);
      }
      await loadUserContext();

      navigate("/setup-representative", {
        state: {
          ...(changeMode && { changeMode: true }),
          accountTypeName: selectedType.name,
          ...additionalData,
        },
      });
    } catch (err) {
      setError(
        getFriendlyApiError(err, "Could not save your account type. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      if (step === 4) {
        setOfficePosition(null);
        setCustomPosition("");
      }
      if (step === 3) setOfficeLevel(null);
      setStep(step - 1);
    }
  };

  const isContinueDisabled = () => {
    if (saving) return true;
    if (step > 1 && !selectedType?.requiresFollowUp) return true;
    if (step === 1) return !selectedType;
    if (step === 2) return !officeStatus;
    if (step === 3) return !officeLevel;
    if (step === 4) {
      if (officePosition === "other") return !customPosition.trim();
      return !officePosition;
    }
    return true;
  };

  const renderStepTitle = () => {
    if (step === 1) return changeMode ? "Change Account Type" : "Choose your account type";
    if (step === 2) return "What is your office status?";
    if (step === 3) return "What level of office?";
    if (step === 4) return "What position?";
    return "";
  };

  const renderStepSubtitle = () => {
    if (step === 1) return changeMode ? "Select a new account type" : "Select the option that best describes you";
    if (step === 2) return "";
    if (step === 3) return "";
    if (step === 4) {
      return "Select the office you currently hold, previously held, or are running for.";
    }
    return "";
  };

  const getButtonText = () =>
    step === 4 || !selectedType?.requiresFollowUp ? "Continue" : "Next";

  const renderOptionCard = (option, isSelected, onSelect) => (
    <button
      type="button"
      onClick={() => onSelect(option)}
      className={`relative w-full text-left rounded-xl border-2 px-4 py-3 transition-all duration-150 cursor-pointer ${
        isSelected
          ? "border-[#047857] bg-emerald-50 shadow-sm"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
            isSelected ? "border-[#047857] bg-[#047857]" : "border-gray-300"
          }`}
        >
          {isSelected && (
            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-gray-900">
              {option.label || option.title}
            </span>
            {option.recommended && (
              <span className="inline-flex items-center rounded-full bg-[#047857] px-2.5 py-0.5 text-xs font-medium text-white">
                Recommended
              </span>
            )}
          </div>
          {(option.sublabel || option.subtitle) && (
            <p className="mt-1 text-base text-gray-900">
              {option.sublabel || option.subtitle}
            </p>
          )}
          {option.helperText && (
            <p className="mt-1 text-sm text-gray-900 font-medium">
              {option.helperText}
            </p>
          )}
        </div>
      </div>
    </button>
  );

  const renderStep1 = () => (
    <div className="flex flex-col gap-3">
      {ACCOUNT_TYPES.map((type) => (
        <Fragment key={type.name}>
          {renderOptionCard(type, selectedType?.name === type.name, handleSelectType)}
        </Fragment>
      ))}
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col gap-3">
      {STATUS_OPTIONS.map((opt) => (
        <Fragment key={opt.value}>
          {renderOptionCard(opt, officeStatus === opt.value, (o) =>
            setOfficeStatus(o.value)
          )}
        </Fragment>
      ))}
    </div>
  );

  const renderStep3 = () => (
    <div className="flex flex-col gap-3">
      {LEVEL_OPTIONS.map((opt) => (
        <Fragment key={opt.value}>
          {renderOptionCard(opt, officeLevel === opt.value, (o) =>
            setOfficeLevel(o.value)
          )}
        </Fragment>
      ))}
    </div>
  );

  const renderStep4 = () => {
    const positions = POSITION_OPTIONS[officeLevel] || [];
    return (
      <div className="flex flex-col gap-3">
        {positions.map((opt) => (
          <Fragment key={opt.value}>
            {renderOptionCard(opt, officePosition === opt.value, (o) =>
              setOfficePosition(o.value)
            )}
          </Fragment>
        ))}
        {officePosition === "other" && (
          <input
            type="text"
            aria-label="Enter your position"
            value={customPosition}
            onChange={(e) => setCustomPosition(e.target.value)}
            placeholder="Enter your position"
            className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3 text-base text-gray-900 placeholder:text-gray-600 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] transition-colors"
            autoCapitalize="words"
          />
        )}
      </div>
    );
  };

  const totalSteps = selectedType?.requiresFollowUp ? 4 : 1;
  const stepSubtitle = renderStepSubtitle();

  return (
    <div className="min-h-screen bg-white flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {(step > 1 || changeMode) && (
          <button
            type="button"
            onClick={() => (step > 1 ? handleBack() : navigate(cancelDestination))}
            className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:text-[#047857] transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {step > 1 ? "Back" : "Cancel"}
          </button>
        )}

        {totalSteps > 1 && (
          <div className="mb-6 flex gap-1.5">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i < step ? "bg-[#047857]" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        )}

        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {renderStepTitle()}
          </h1>
          {stepSubtitle && (
            <p className="mt-2 text-base text-gray-900">{stepSubtitle}</p>
          )}
        </div>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}

        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <button
          ref={nextButtonRef}
          type="button"
          onClick={handleContinue}
          disabled={isContinueDisabled()}
          className={`mt-8 w-full rounded-xl py-3.5 text-base font-semibold text-white transition-all duration-150 ${
            isContinueDisabled()
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-[#047857] hover:bg-[#065f46] active:bg-[#064e3b]"
          }`}
        >
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving…
            </span>
          ) : (
            getButtonText()
          )}
        </button>

        {step === 1 && (
          <p className="mt-5 text-center text-sm text-gray-900">
            You can change this later in settings
          </p>
        )}
      </div>
    </div>
  );
}
